import { Directive } from 'vue';

const SWIPE_DISTANCE_RATIO = 0.25;
const SWIPE_VELOCITY = 0.3;
const SLIDE_DURATION = 200;

interface PanEvent {
  deltaX: number;
  velocityX: number;
  pointerType: string;
}

type SwipeHandler = (event: { deltaX: number }) => void;

interface PanRecognizer {
  set(options: { enable: boolean }): void;
}

interface Swipeable {
  handler: SwipeHandler | null;
  recognizer?: PanRecognizer;
  destroy?: () => void;
}

const swipeables = new WeakMap<HTMLElement, Swipeable>();

function swipingContent() {
  return [...document.querySelectorAll<HTMLElement>('[data-swipe-along]')];
}

function follow(deltaX: number) {
  swipingContent().forEach((content) => {
    content.style.transform = deltaX ? `translateX(${deltaX}px)` : '';
  });
}

function slide(from: number, to: number) {
  return Promise.all(
    swipingContent().map((content) =>
      content
        .animate(
          [
            { transform: `translateX(${from}px)` },
            { transform: `translateX(${to}px)` },
          ],
          { duration: SLIDE_DURATION, easing: 'ease-out', fill: 'forwards' },
        )
        .finished.catch(() => undefined),
    ),
  );
}

function isSwipe(el: HTMLElement, event: PanEvent) {
  return (
    event.deltaX !== 0 &&
    (Math.abs(event.deltaX) > el.offsetWidth * SWIPE_DISTANCE_RATIO ||
      Math.abs(event.velocityX) > SWIPE_VELOCITY)
  );
}

async function settle(
  el: HTMLElement,
  event: PanEvent,
  onSwipe: SwipeHandler | null,
) {
  follow(0);
  if (onSwipe) {
    const exit = Math.sign(event.deltaX) * el.offsetWidth;
    await slide(event.deltaX, exit);
    onSwipe({ deltaX: event.deltaX });
    await slide(-exit, 0);
  } else {
    await slide(event.deltaX, 0);
  }
  swipingContent().forEach((content) =>
    content.getAnimations().forEach((animation) => animation.cancel()),
  );
}

function isTouch(event: PanEvent) {
  return event.pointerType === 'touch';
}

export const swipeDirective: Directive<HTMLElement, SwipeHandler | null> = {
  async mounted(el, binding) {
    const swipeable: Swipeable = { handler: binding.value };
    swipeables.set(el, swipeable);
    const { default: Hammer } = await import('hammerjs');
    if (swipeables.get(el) !== swipeable) {
      return;
    }
    const manager = new Hammer.Manager(el, {
      recognizers: [[Hammer.Pan, { direction: Hammer.DIRECTION_HORIZONTAL }]],
    });
    manager.on('panmove', (event: PanEvent) => {
      if (isTouch(event)) {
        follow(event.deltaX);
      }
    });
    manager.on('panend', (event: PanEvent) => {
      if (isTouch(event)) {
        settle(el, event, isSwipe(el, event) ? swipeable.handler : null);
      }
    });
    manager.on('pancancel', (event: PanEvent) => {
      if (isTouch(event)) {
        settle(el, event, null);
      }
    });
    swipeable.recognizer = manager.get('pan');
    swipeable.recognizer.set({ enable: Boolean(swipeable.handler) });
    swipeable.destroy = () => manager.destroy();
  },
  updated(el, binding) {
    const swipeable = swipeables.get(el);
    if (swipeable && swipeable.handler !== binding.value) {
      swipeable.handler = binding.value;
      swipeable.recognizer?.set({ enable: Boolean(binding.value) });
    }
  },
  unmounted(el) {
    swipeables.get(el)?.destroy?.();
    swipeables.delete(el);
  },
};
